import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../../firebase";
import { format } from "date-fns";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Link } from "react-router-dom";

// Custom blue marker
const markerIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

export default function ShiftManager() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [form, setForm] = useState({});
  const [showMap, setShowMap] = useState(false);

  // Load shifts from Firestore
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const q = query(collection(db, "shifts"), orderBy("date", "desc"));
      const snap = await getDocs(q);
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setShifts(data);
      setLoading(false);
    };
    load();
  }, []);

  const openModal = (shift) => {
    setSelectedShift(shift);
    setForm({
      clientName: shift.clientName || "",
      staffName: shift.staffName || "",
      startTime: shift.startTime || "",
      endTime: shift.endTime || "",
      totalHours: shift.totalHours || 0,
      rate: shift.rate || 0,
      notes: shift.notes || "",
      status: shift.status || "Assigned",
    });
    setShowMap(false);
  };

  const closeModal = () => {
    setSelectedShift(null);
    setForm({});
    setShowMap(false);
  };

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const saveShift = async () => {
    if (!selectedShift) return;
    const ref = doc(db, "shifts", selectedShift.id);
    await updateDoc(ref, form);
    setShifts((prev) =>
      prev.map((s) => (s.id === selectedShift.id ? { ...s, ...form } : s))
    );
    closeModal();
    alert("✅ Shift updated!");
  };

  const deleteShift = async () => {
    if (!selectedShift) return;
    const ref = doc(db, "shifts", selectedShift.id);
    await deleteDoc(ref);
    setShifts((prev) => prev.filter((s) => s.id !== selectedShift.id));
    closeModal();
    alert("🗑️ Shift deleted!");
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-700">
          Manage Shifts
        </h1>

        {/* Link back to Rota Editor */}
        <Link
          to="/admin/rota"
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm"
        >
          ← Back to Rota Editor
        </Link>
      </div>

      {loading ? (
        <p className="text-center text-gray-500">Loading shifts...</p>
      ) : (
        <div className="overflow-x-auto bg-white rounded-xl shadow-md">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-gray-200 text-gray-700">
              <tr>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">Client</th>
                <th className="p-3 text-left">Staff</th>
                <th className="p-3 text-left">Shift</th>
                <th className="p-3 text-center">Hours</th>
                <th className="p-3 text-center">Rate</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shifts.map((s) => (
                <tr
                  key={s.id}
                  className="border-b hover:bg-gray-50 cursor-pointer"
                  onClick={() => openModal(s)}
                >
                  <td className="p-3">{s.date}</td>
                  <td className="p-3">{s.clientName}</td>
                  <td className="p-3">{s.staffName}</td>
                  <td className="p-3">
                    {s.startTime}–{s.endTime}
                  </td>
                  <td className="p-3 text-center">{s.totalHours}</td>
                  <td className="p-3 text-center">£{s.rate}</td>
                  <td className="p-3 text-center text-blue-600 underline">
                    Edit
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {shifts.length === 0 && (
            <p className="text-center text-gray-500 p-4">
              No shifts available.
            </p>
          )}
        </div>
      )}

      {/* Edit/Delete Modal */}
      {selectedShift && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
          <div className="bg-white rounded-xl shadow-lg w-[420px] p-6 relative">
            <h2 className="text-lg font-semibold mb-4 text-gray-700">
              Edit Shift ({format(new Date(selectedShift.date), "EEE dd MMM")})
            </h2>

            <div className="space-y-3">
              <input
                name="clientName"
                value={form.clientName}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="Client Name"
              />
              <input
                name="staffName"
                value={form.staffName}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="Staff Name"
              />
              <div className="flex gap-2">
                <input
                  name="startTime"
                  value={form.startTime}
                  onChange={handleChange}
                  className="flex-1 border rounded px-3 py-2 text-sm"
                  placeholder="Start Time (07:30)"
                />
                <input
                  name="endTime"
                  value={form.endTime}
                  onChange={handleChange}
                  className="flex-1 border rounded px-3 py-2 text-sm"
                  placeholder="End Time (14:30)"
                />
              </div>
              <div className="flex gap-2">
                <input
                  name="totalHours"
                  type="number"
                  step="0.5"
                  value={form.totalHours}
                  onChange={handleChange}
                  className="flex-1 border rounded px-3 py-2 text-sm"
                  placeholder="Hours"
                />
                <input
                  name="rate"
                  type="number"
                  value={form.rate}
                  onChange={handleChange}
                  className="flex-1 border rounded px-3 py-2 text-sm"
                  placeholder="Rate"
                />
              </div>
              <input
                name="notes"
                value={form.notes}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="Notes"
              />
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm"
              >
                <option value="Assigned">Assigned</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>

            {selectedShift.clientLocation && (
              <div className="mt-4">
                <button
                  onClick={() => setShowMap((p) => !p)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded w-full"
                >
                  {showMap ? "Hide Map" : "View on Map"}
                </button>

                {showMap && (
                  <div className="mt-3 border rounded overflow-hidden">
                    <MapContainer
                      center={[
                        selectedShift.clientLocation.lat,
                        selectedShift.clientLocation.lng,
                      ]}
                      zoom={14}
                      style={{ height: "250px", width: "100%" }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <Marker
                        position={[
                          selectedShift.clientLocation.lat,
                          selectedShift.clientLocation.lng,
                        ]}
                        icon={markerIcon}
                      >
                        <Popup>
                          {selectedShift.clientName} <br />{" "}
                          {selectedShift.staffName}
                        </Popup>
                      </Marker>
                    </MapContainer>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between mt-5">
              <button
                onClick={deleteShift}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
              >
                🗑 Delete
              </button>
              <div className="flex gap-2">
                <button
                  onClick={closeModal}
                  className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={saveShift}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
                >
                  💾 Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
